import React, { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import SuperadminLayout from './SuperadminLayout';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import { ordenarListaAlfabetica } from './superadminSortUtils';
import { labelTipoUsuario, TIPO_USUARIO_LABEL } from '../../../resources/tipoUsuarioLabels';
import './SuperadminStyles.css';

const UsuariosCliente = () => {
    const history = useHistory();
    const { clienteId } = useParams();
    const [cliente, setCliente] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [filteredUsuarios, setFilteredUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('todos');
    const [tipoFilter, setTipoFilter] = useState('todos');
    const [currentPage, setCurrentPage] = useState(1);
    const [showEstadoModal, setShowEstadoModal] = useState(false);
    const [showRevealModal, setShowRevealModal] = useState(false);
    const [usuarioToToggle, setUsuarioToToggle] = useState(null);
    const [usuarioToReveal, setUsuarioToReveal] = useState(null);
    const [revealedPassword, setRevealedPassword] = useState('');
    const [revealing, setRevealing] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const itemsPerPage = 20;

    useEffect(() => {
        const token = localStorage.getItem('superadmin_token');
        const user = localStorage.getItem('superadmin_user');

        if (!token || !user) {
            history.push('/superadmin/login');
            return undefined;
        }

        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        let cancelled = false;

        const fetchClienteYUsuarios = async () => {
            try {
                const [clienteResponse, usuariosResponse] = await Promise.all([
                    axios.get(`/superadmin/clientes/${clienteId}`),
                    axios.get(`/superadmin/clientes/${clienteId}/usuarios`)
                ]);
                if (cancelled) return;

                setCliente(clienteResponse.data);
                const usuariosData = Array.isArray(usuariosResponse.data) ? usuariosResponse.data : [];
                setUsuarios(usuariosData);
                setFilteredUsuarios(usuariosData);
                setLoading(false);
            } catch (error) {
                if (cancelled) return;
                console.error('Error fetching data:', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('superadmin_token');
                    localStorage.removeItem('superadmin_user');
                    history.push('/superadmin/login');
                }
                setUsuarios([]);
                setFilteredUsuarios([]);
                setLoading(false);
            }
        };

        setLoading(true);
        fetchClienteYUsuarios();

        return () => {
            cancelled = true;
        };
    }, [history, clienteId]);

    useEffect(() => {
        // Asegurar que usuarios sea un array antes de filtrar
        if (!Array.isArray(usuarios)) {
            setFilteredUsuarios([]);
            return;
        }

        let filtered = usuarios;

        // Filtrar por estado (activo: 1=acceso al sistema, 0=personal sin acceso)
        if (estadoFilter !== 'todos') {
            const activoValue = estadoFilter === 'activo' ? 1 : 0;
            filtered = filtered.filter((usuario) => Number(usuario.activo) === activoValue);
        }

        // Filtrar por tipo de usuario
        if (tipoFilter !== 'todos') {
            filtered = filtered.filter((usuario) => String(usuario.tipo_usuario) === tipoFilter);
        }

        // Filtrar por búsqueda
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            filtered = filtered.filter((usuario) =>
                (usuario.name && usuario.name.toLowerCase().includes(q)) ||
                (usuario.lastname && usuario.lastname.toLowerCase().includes(q)) ||
                (usuario.nombre_completo && usuario.nombre_completo.toLowerCase().includes(q)) ||
                (usuario.email && usuario.email.toLowerCase().includes(q))
            );
        }

        const conNombre = filtered.map((u) => ({
            ...u,
            _orden: u.nombre_completo || [u.name, u.lastname].filter(Boolean).join(' '),
        }));
        setFilteredUsuarios(ordenarListaAlfabetica(conNombre, '_orden'));
        setCurrentPage(1);
    }, [searchTerm, estadoFilter, tipoFilter, usuarios]);

    const renderTipoBadge = (usuario) => {
        if (!usuario) return <span className="text-muted">—</span>;
        if (Number(usuario.activo) !== 1) {
            return <span className="badge badge-secondary" title="Personal sin acceso">Sin acceso</span>;
        }
        if (String(usuario.usuario_actualizado) !== '1') {
            return <span className="badge badge-light border" title="Acceso pendiente de actualización">Pendiente</span>;
        }
        return String(usuario.tipo_usuario) === '1'
            ? <span className="badge badge-warning text-dark">{labelTipoUsuario(usuario.tipo_usuario, true)}</span>
            : <span className="badge badge-info">{labelTipoUsuario(usuario.tipo_usuario, true)}</span>;
    };

    const renderActivoBadge = (usuario) => {
        if (!usuario) return <span className="text-muted">—</span>;
        return (
            <span className={`badge badge-${Number(usuario.activo) === 1 ? 'success' : 'secondary'}`}>
                {Number(usuario.activo) === 1 ? 'Activo' : 'Inactivo'}
            </span>
        );
    };

    const isUsuarioActivo = (usuario) => Boolean(usuario) && Number(usuario.activo) === 1;

    const refetchData = async () => {
        try {
            const [clienteResponse, usuariosResponse] = await Promise.all([
                axios.get(`/superadmin/clientes/${clienteId}`),
                axios.get(`/superadmin/clientes/${clienteId}/usuarios`)
            ]);
            setCliente(clienteResponse.data);
            const usuariosData = Array.isArray(usuariosResponse.data) ? usuariosResponse.data : [];
            setUsuarios(usuariosData);
            setFilteredUsuarios(usuariosData);
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    };

    const handleToggleEstadoClick = (usuario) => {
        setUsuarioToToggle(usuario);
        setShowEstadoModal(true);
    };

    const handleToggleEstadoConfirm = async () => {
        if (!usuarioToToggle) return;
        try {
            const nuevoActivo = isUsuarioActivo(usuarioToToggle) ? 0 : 1;
            await axios.put(`/superadmin/usuarios/${usuarioToToggle.id}/estado`, {
                estado: nuevoActivo === 1
            });
            setAlert({
                show: true,
                type: 'success',
                message: `Usuario ${nuevoActivo === 1 ? 'activado' : 'desactivado'} exitosamente`
            });
            await refetchData();
        } catch (error) {
            console.error('Error toggling estado:', error);
            const errData = error.response?.data;
            const msg =
                errData?.errors?.tipo_usuario?.[0] ||
                errData?.message ||
                'Error al cambiar el estado del usuario';
            setAlert({
                show: true,
                type: 'danger',
                message: msg
            });
        } finally {
            setShowEstadoModal(false);
            setUsuarioToToggle(null);
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        }
    };

    const handleRevealClick = (usuario) => {
        setUsuarioToReveal(usuario);
        setRevealedPassword('');
        setShowRevealModal(true);
    };

    const handleRevealConfirm = async () => {
        if (!usuarioToReveal) return;
        setRevealing(true);
        try {
            const response = await axios.get(`/superadmin/macros-passwords/${usuarioToReveal.id}/reveal`);
            setRevealedPassword(response.data.password || '');
        } catch (error) {
            setShowRevealModal(false);
            setUsuarioToReveal(null);
            setAlert({
                show: true,
                type: 'danger',
                message: error.response?.data?.message || 'No hay contraseña almacenada para este usuario.',
            });
            setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
        } finally {
            setRevealing(false);
        }
    };

    const handleCloseRevealModal = () => {
        setShowRevealModal(false);
        setUsuarioToReveal(null);
        setRevealedPassword('');
    };

    const cupoMedicos = cliente
        ? `${cliente.usuarios_medicos_creados ?? 0}/${cliente.usuarios_medicos_disponibles ?? 0}`
        : '—';
    const cupoAdmin = cliente
        ? `${cliente.usuarios_administrativos_creados ?? 0}/${cliente.usuarios_administrativos_disponibles ?? 0}`
        : '—';
    const cupoUsados =
        cliente != null && Number.isFinite(Number(cliente.usuarios_actuales))
            ? Number(cliente.usuarios_actuales)
            : usuarios.filter((u) => Number(u.activo) === 1).length;
    const limiteTotal = cliente
        ? (Number(cliente.usuarios_medicos_disponibles ?? 0) + Number(cliente.usuarios_administrativos_disponibles ?? 0)) || Number(cliente.limite_usuarios ?? 0)
        : 0;
    const limiteAlcanzado = cliente && cupoUsados >= limiteTotal;

    // Paginación - asegurar que filteredUsuarios sea un array
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = Array.isArray(filteredUsuarios) ? filteredUsuarios.slice(indexOfFirstItem, indexOfLastItem) : [];
    const totalPages = Array.isArray(filteredUsuarios) ? Math.ceil(filteredUsuarios.length / itemsPerPage) : 0;

    if (loading) {
        return (
            <SuperadminLayout>
                <div className="loading-container">
                    <div className="spinner-border text-info" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                </div>
            </SuperadminLayout>
        );
    }

    return (
        <SuperadminLayout>
                            {/* Header con info del cliente */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h4><i className="fas fa-users"></i> Usuarios de {cliente?.nombre}</h4>
                                        <p className="text-muted mb-0">
                                            NIT: {cliente?.nit} | Email: {cliente?.email_contacto}
                                        </p>
                                    </div>
                                    <Link to={`/superadmin/clientes/${clienteId}/informacion`} className="btn btn-secondary">
                                        <i className="fas fa-arrow-left"></i> Volver al cliente
                                    </Link>
                                </div>
                                
                                <div className="alert alert-info">
                                    <strong>
                                        <i className="fas fa-info-circle"></i> Usuarios activos: {cupoUsados} de {limiteTotal}
                                    </strong>
                                    <span className="text-muted ml-2">
                                        {labelTipoUsuario('0', true)} {cupoMedicos} · {labelTipoUsuario('1', true)} {cupoAdmin}
                                    </span>
                                    {limiteAlcanzado && (
                                        <span className="text-danger ml-3">
                                            <i className="fas fa-exclamation-triangle"></i> Cupos activos agotados
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5>Listado de Usuarios</h5>
                                <Link
                                    to={`/superadmin/clientes/${clienteId}/usuarios/nuevo`}
                                    className={`btn btn-superadmin ${limiteAlcanzado ? 'disabled' : ''}`}
                                    onClick={(e) => limiteAlcanzado && e.preventDefault()}
                                    title={limiteAlcanzado ? 'Límite de usuarios alcanzado' : 'Crear nuevo usuario'}
                                >
                                    <i className="fas fa-plus"></i> Crear Usuario
                                </Link>
                            </div>

                            {/* Alertas */}
                            {alert.show && (
                                <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
                                    {alert.message}
                                    <button type="button" className="close" onClick={() => setAlert({ show: false, type: '', message: '' })}>
                                        <span>&times;</span>
                                    </button>
                                </div>
                            )}

                            {/* Filtros */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Buscar por nombre o email..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-control"
                                        value={estadoFilter}
                                        onChange={(e) => setEstadoFilter(e.target.value)}
                                    >
                                        <option value="todos">Todos los estados</option>
                                        <option value="activo">Activos</option>
                                        <option value="inactivo">Inactivos</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-control"
                                        value={tipoFilter}
                                        onChange={(e) => setTipoFilter(e.target.value)}
                                    >
                                        <option value="todos">Todos los tipos</option>
                                        <option value="0">{TIPO_USUARIO_LABEL['0']}</option>
                                        <option value="1">{TIPO_USUARIO_LABEL['1']}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Tabla */}
                            <div className="table-responsive">
                                <table className="table table-hover superadmin-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Nombre</th>
                                            <th>Email</th>
                                            <th>Tipo</th>
                                            <th>Acceso</th>
                                            <th>Actualizado</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map(usuario => (
                                                <tr key={usuario.id}>
                                                    <td>{usuario.id}</td>
                                                    <td>{usuario.nombre_completo || `${usuario.name} ${usuario.lastname}`}</td>
                                                    <td>{usuario.email}</td>
                                                    <td>{renderTipoBadge(usuario)}</td>
                                                    <td>{renderActivoBadge(usuario)}</td>
                                                    <td>
                                                        {usuario.updated_at
                                                            ? new Date(usuario.updated_at).toLocaleDateString()
                                                            : <span className="text-muted">—</span>}
                                                    </td>
                                                    <td>
                                                        <Link
                                                            to={`/superadmin/usuarios/editar/${usuario.id}`}
                                                            className="btn btn-sm btn-dark mr-1"
                                                            title="Editar"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </Link>
                                                        {Number(usuario.activo) === 1 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger mr-1"
                                                                onClick={() => handleRevealClick(usuario)}
                                                                title="Ver contraseña almacenada"
                                                            >
                                                                <i className="fas fa-key"></i>
                                                            </button>
                                                        )}
                                                        <button
                                                            className={`btn btn-sm btn-${isUsuarioActivo(usuario) ? 'danger' : 'success'}`}
                                                            onClick={() => handleToggleEstadoClick(usuario)}
                                                            title={isUsuarioActivo(usuario) ? 'Desactivar acceso' : 'Activar acceso'}
                                                        >
                                                            <i className={`fas fa-${isUsuarioActivo(usuario) ? 'ban' : 'check'}`}></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center">
                                                    No se encontraron usuarios
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Paginación */}
                            {totalPages > 1 && (
                                <nav>
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                Anterior
                                            </button>
                                        </li>
                                        {[...Array(totalPages)].map((_, index) => (
                                            <li
                                                key={index + 1}
                                                className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => setCurrentPage(index + 1)}
                                                >
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                disabled={currentPage === totalPages}
                                            >
                                                Siguiente
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
            {/* Modal de Confirmación Estado */}
            <Modal show={showEstadoModal && Boolean(usuarioToToggle)} onHide={() => setShowEstadoModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Cambio de Estado</Modal.Title>
                </Modal.Header>
                {usuarioToToggle && (
                <>
                <Modal.Body>
                    ¿Está seguro que desea {isUsuarioActivo(usuarioToToggle) ? 'desactivar el acceso de' : 'activar'} al usuario{' '}
                    <strong>
                        {usuarioToToggle.nombre_completo ||
                            `${usuarioToToggle.name || ''} ${usuarioToToggle.lastname || ''}`.trim()}
                    </strong>
                    ?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEstadoModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant={isUsuarioActivo(usuarioToToggle) ? 'danger' : 'success'}
                        onClick={handleToggleEstadoConfirm}
                    >
                        {isUsuarioActivo(usuarioToToggle) ? 'Desactivar' : 'Activar'}
                    </Button>
                </Modal.Footer>
                </>
                )}
            </Modal>

            {/* Modal ver contraseña (vault macros) */}
            <Modal show={showRevealModal} onHide={handleCloseRevealModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ver contraseña</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {usuarioToReveal && (
                        <p>
                            Usuario:{' '}
                            <strong>
                                {usuarioToReveal.nombre_completo ||
                                    `${usuarioToReveal.name || ''} ${usuarioToReveal.lastname || ''}`.trim()}
                            </strong>
                            <br />
                            Email: <strong>{usuarioToReveal.email}</strong>
                        </p>
                    )}
                    {!revealedPassword ? (
                        <p className="text-muted mb-0">
                            Esta consulta queda registrada en auditoría. ¿Continuar?
                        </p>
                    ) : (
                        <div className="alert alert-dark mb-0">
                            <strong>Contraseña:</strong>
                            <div className="mt-2 font-monospace">{revealedPassword}</div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseRevealModal}>
                        Cerrar
                    </Button>
                    {!revealedPassword && (
                        <Button variant="danger" onClick={handleRevealConfirm} disabled={revealing}>
                            {revealing ? 'Consultando…' : 'Revelar contraseña'}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </SuperadminLayout>
    );
};

export default UsuariosCliente;
